import { Link } from "react-router-dom";
import { Grid3x3, Flame, ArrowRight, CheckCircle2 } from "@/lib/icons";
import { useEffect, useState } from "react";
import {
  getDailyConfig,
  loadProgress,
  loadResult,
  loadStreak,
  todayKey,
  type DailyConfig,
  type DailyResult,
} from "./daily";

/**
 * Compact "Chem-doku Puzzle of the Day" teaser. Mirrors DailyElementCard
 * styling so the two sit side-by-side cleanly on the homepage. Hydration-
 * safe: localStorage reads happen after mount.
 */
const DIFFICULTY_LABEL: Record<string, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
};

export default function ChemDokuDailyCard() {
  const [hydrated, setHydrated] = useState(false);
  const [config, setConfig] = useState<DailyConfig | null>(null);
  const [result, setResult] = useState<DailyResult | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [streak, setStreak] = useState<{ current: number; best: number }>({
    current: 0,
    best: 0,
  });

  useEffect(() => {
    const cfg = getDailyConfig();
    setConfig(cfg);
    const key = todayKey();
    const res = loadResult(key);
    setResult(res);
    if (!res) {
      const prog = loadProgress(key);
      setInProgress(!!prog && prog.moves > 0);
    }
    setStreak(loadStreak());
    setHydrated(true);
  }, []);

  const solved = !!result;
  const sizeLabel = config ? `${config.size}x${config.size}` : "--";

  return (
    <Link
      to="/play/chem-doku"
      className="group relative flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-accent/30 transition-colors"
      aria-label={`Chem-doku puzzle of the day${solved ? " - solved" : ""}`}
    >
      {/* Mini-grid tile */}
      <div className="flex-shrink-0 w-16 h-16 rounded-lg border border-primary/30 bg-primary/5 flex flex-col items-center justify-center font-mono">
        {hydrated && solved ? (
          <>
            <CheckCircle2
              size={14}
              className="text-primary mb-0.5"
              aria-hidden="true"
            />
            <span className="text-[10px] text-muted-foreground leading-none">
              {result?.moves} moves
            </span>
          </>
        ) : (
          <Grid3x3 size={22} className="text-primary/70" aria-hidden="true" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
            Daily
          </span>
          {hydrated && streak.current > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-orange-500 dark:text-orange-400">
              <Flame size={10} aria-hidden="true" />
              {streak.current}-day streak
            </span>
          )}
        </div>
        <div className="text-sm font-semibold text-foreground truncate">
          {hydrated && config
            ? `Chem-doku - ${sizeLabel} ${DIFFICULTY_LABEL[config.difficulty] ?? ""}`
            : "Chem-doku Puzzle of the Day"}
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {hydrated && solved ? (
            <span className="inline-flex items-center gap-1">
              <CheckCircle2 size={11} aria-hidden="true" />
              You solved today - new puzzle at midnight
            </span>
          ) : hydrated && inProgress ? (
            "In progress - resume where you left off"
          ) : hydrated && config ? (
            config.pack.label
          ) : (
            "Sudoku with element symbols. Same puzzle for everyone."
          )}
        </div>
      </div>

      <ArrowRight
        size={16}
        className="flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors"
        aria-hidden="true"
      />
    </Link>
  );
}